import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class AmazonSettings extends ModelObject<AmazonSettings> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    private _defaultDeliveryStockItemId: Guid | null = null;
    /**
    * Default Stock Item Id to be used for delivery charge of imported orders. This will be used if no BrewMan product name matches the Shipping Option Name in Amazon
    */
    public get defaultDeliveryStockItemId(): Guid | null { return this._defaultDeliveryStockItemId }
    public set defaultDeliveryStockItemId(value: Guid | null) {
      this._defaultDeliveryStockItemId = value === undefined ? null : value;
    }
    private _ignoreBeforeDate: LocalDate | null = null;
    /**
    * If provided then Orders on Amazon from before this date will be ignored.
    */
    public get ignoreBeforeDate(): LocalDate | null { return this._ignoreBeforeDate }
    public set ignoreBeforeDate(value: LocalDate | null) {
      this._ignoreBeforeDate = value === undefined ? null : value;
    }
    private _outletId: Guid | null = null;
    /**
    * Outlet Id for all Amazon orders import to a single outlet. Should be null only if tenant doesn't use Amazon.
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    /**
    * The identifier for the selling partner Amazon account of this tenant.
    * Not Nullable
    * Preserves leading and trailing whitespce.
    */
    public amazonSellerPartnerId: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.defaultDeliveryStockItemId = jsonSerializedObject.defaultDeliveryStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultDeliveryStockItemId);
          this.ignoreBeforeDate = jsonSerializedObject.ignoreBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.ignoreBeforeDate);
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.amazonSellerPartnerId = jsonSerializedObject.amazonSellerPartnerId;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.defaultDeliveryStockItemId = this.defaultDeliveryStockItemId == null ? null : this.defaultDeliveryStockItemId.toString();
        toRet.ignoreBeforeDate = this.ignoreBeforeDate == null ? null : this.ignoreBeforeDate.toString();
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.runInBackground = this.runInBackground;
        toRet.amazonSellerPartnerId = this.amazonSellerPartnerId;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "AmazonSettings.Id cannot be empty"},
            {field: "defaultDeliveryStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "AmazonSettings.DefaultDeliveryStockItemId cannot be empty"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "AmazonSettings.OutletId cannot be empty"},
    ];
}


export class AmazonSettingsService extends ServiceBase {

  public async getAmazonSettings(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AmazonSettings> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => new AmazonSettings(response.amazonSettings);;
    return this.makeJsonRequest("amazonSettings/v1/getAmazonSettings", req, false, extractor, opts);
  };

  public async saveAmazonSettings(tenantId: Guid, amazonSettings: AmazonSettings, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    amazonSettings:amazonSettings.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("amazonSettings/v1/saveAmazonSettings", req, true, extractor, opts);
  };

  public async hardDeleteAmazonSettings(tenantId: Guid, amazonSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    amazonSettingsId:amazonSettingsId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("amazonSettings/v1/hardDeleteAmazonSettings", req, true, extractor, opts);
  };

  public async createAmazonLoginUrl(tenantId: Guid, amazonSettingsId: Guid, returnPageUrl: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:tenantId.toString(),
    amazonSettingsId:amazonSettingsId.toString(),
    returnPageUrl:returnPageUrl,
    };
    const extractor = (response:any) => response.url;;
    return this.makeJsonRequest("amazonSettings/v1/createAmazonLoginUrl", req, false, extractor, opts);
  };

  public async completeAmazonAuthentication(tenantId: Guid, amazonSettingsId: Guid, queryString: string, returnPageUrl: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<AmazonSettings> {
    const req = {
    tenantId:tenantId.toString(),
    amazonSettingsId:amazonSettingsId.toString(),
    queryString:queryString,
    returnPageUrl:returnPageUrl,
    };
    const extractor = (response:any) => new AmazonSettings(response.amazonSettings);;
    return this.makeJsonRequest("amazonSettings/v1/completeAmazonAuthentication", req, true, extractor, opts);
  };

  public async isAmazonAuthenticated(tenantId: Guid, amazonSettingsId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<boolean> {
    const req = {
    tenantId:tenantId.toString(),
    amazonSettingsId:amazonSettingsId.toString(),
    };
    const extractor = (response:any) => response.authenticated;;
    return this.makeJsonRequest("amazonSettings/v1/isAmazonAuthenticated", req, false, extractor, opts);
  };
}

export const amazonSettingsService = new AmazonSettingsService();